// Склад сырья (Этап 3): общие части для раздела «Склад» и экрана оператора — остатки, приход, списание, инвентаризация.

import { useState } from "react";
import { fmtDateTime, fmtNum } from "../../shared/format";
import { Button, Field, Input, Loading, Modal, Select, useAsync, useToast } from "../../shared/ui";
import { api, type Role } from "../ctx";

export const KIND: Record<string, string> = { cement: "Цемент", sand: "Песок", gravel: "Щебень", water: "Вода", additive: "Добавка", other: "Прочее" };
export const LEVEL: Record<string, { label: string; cls: string }> = {
  ok: { label: "в норме", cls: "s-done" },
  soon: { label: "заканчивается", cls: "s-transit" },
  low: { label: "ниже минимума", cls: "s-transit" },
  out: { label: "нет на складе", cls: "s-conflict" },
};
export const LAB: Record<string, { label: string; cls: string }> = {
  not_required: { label: "без контроля", cls: "" },
  pending: { label: "на проверке", cls: "s-transit" },
  accepted: { label: "годен", cls: "s-done" },
  rejected: { label: "не годен", cls: "s-conflict" },
};

export const can = {
  receipt: (r: Role) => ["operator", "lab", "dispatcher", "admin"].includes(r),
  inventory: (r: Role) => ["operator", "lab", "admin"].includes(r),
  lab: (r: Role) => ["lab", "admin"].includes(r),
  cancelReceipt: (r: Role) => ["lab", "dispatcher", "admin"].includes(r),
};

export type Balance = {
  plant_id: string; plant_name: string; material_id: string; material: string; kind: string; unit: string; balance: number;
  min_stock: number | null; level: string; daily_use: number; days_left: number | null; pending_lab: number;
};

export function qty(v: number, unit: string): string {
  return `${fmtNum(v, unit === "т" || unit === "м³" ? 1 : 0)} ${unit}`;
}

export function BalancesTable({ rows, showPlant }: { rows: Balance[]; showPlant?: boolean }) {
  if (!rows.length) return <p className="empty">Движений сырья ещё нет — оформите первый приход.</p>;
  return (
    <div className="panel table-wrap">
      <table className="table">
        <thead><tr>{showPlant && <th>Завод</th>}<th>Материал</th><th className="num">Остаток</th><th>Состояние</th><th className="num">Расход в сутки</th><th className="num">Хватит на</th><th className="num">На проверке</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.plant_id + r.material_id}>
              {showPlant && <td>{r.plant_name}</td>}
              <td>{r.material}<div className="small muted">{KIND[r.kind] || r.kind}{r.min_stock != null ? ` · минимум ${qty(r.min_stock, r.unit)}` : ""}</div></td>
              <td className="num strong">{qty(r.balance, r.unit)}</td>
              <td><span className={`tag ${LEVEL[r.level]?.cls}`}>{LEVEL[r.level]?.label}</span></td>
              <td className="num">{r.daily_use ? qty(r.daily_use, r.unit) : "—"}</td>
              <td className={`num${r.level !== "ok" ? " strong" : ""}`}>{r.days_left == null ? "—" : `${fmtNum(r.days_left, 1)} дн.`}</td>
              <td className="num">{r.pending_lab ? qty(r.pending_lab, r.unit) : ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function useMaterials() {
  return useAsync<any>(() => api.get("/materials", { limit: 500 }), []);
}

export function ReceiptDialog({ plants, plantId, onClose, onDone }: { plants: { id: string; name: string }[]; plantId?: string; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const mats = useMaterials();
  const [v, setV] = useState({ plant_id: plantId || plants[0]?.id || "", material_id: "", qty: "", supplier: "", document_no: "", vehicle_plate: "", comment: "" });
  const m = (mats.data?.items || []).find((x: any) => x.id === v.material_id);
  const set = (k: string, x: string) => setV({ ...v, [k]: x });
  return (
    <Modal title="Приход сырья" onClose={onClose} actions={<>
      <Button onClick={onClose}>Отмена</Button>
      <Button variant="primary" disabled={!v.plant_id || !v.material_id || !(Number(v.qty.replace(",", ".")) > 0)} onClick={async () => {
        try {
          const r = await api.post("/stock/receipts", { ...v, qty: Number(v.qty.replace(",", ".")) });
          toast.ok(r.lab_status === "pending" ? "Приход оформлен — партия ждёт входного контроля лаборатории" : "Приход оформлен");
          onDone();
        } catch (e) { toast.error(e); }
      }}>Оформить приход</Button>
    </>}>
      {!mats.data ? <Loading error={mats.error} /> : (
        <div className="form-grid">
          {plants.length > 1 && <Field label="Завод"><Select value={v.plant_id} onChange={(e) => set("plant_id", e.target.value)} options={plants.map((p) => ({ value: p.id, label: p.name }))} /></Field>}
          <Field label="Материал" wide><Select value={v.material_id} onChange={(e) => set("material_id", e.target.value)} placeholder="Выберите"
            options={mats.data.items.filter((x: any) => x.active).map((x: any) => ({ value: x.id, label: `${x.name} (${x.unit})` }))} /></Field>
          <Field label={`Количество${m ? `, ${m.unit}` : ""}`}><Input inputMode="decimal" value={v.qty} onChange={(e) => set("qty", e.target.value)} /></Field>
          <Field label="Поставщик"><Input value={v.supplier} onChange={(e) => set("supplier", e.target.value)} /></Field>
          <Field label="Документ (накладная)"><Input value={v.document_no} onChange={(e) => set("document_no", e.target.value)} /></Field>
          <Field label="Машина"><Input value={v.vehicle_plate} onChange={(e) => set("vehicle_plate", e.target.value)} placeholder="Госномер" /></Field>
          <Field label="Комментарий" wide><Input value={v.comment} onChange={(e) => set("comment", e.target.value)} /></Field>
          {m?.incoming_control && <p className="small muted wide" style={{ margin: 0 }}>У этого материала входной контроль: партия попадёт в остаток сразу, но будет «на проверке», пока лаборатория не решит.</p>}
        </div>
      )}
    </Modal>
  );
}

export function WriteoffDialog({ plants, plantId, onClose, onDone }: { plants: { id: string; name: string }[]; plantId?: string; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const mats = useMaterials();
  const [v, setV] = useState({ plant_id: plantId || plants[0]?.id || "", material_id: "", qty: "", reason: "" });
  const set = (k: string, x: string) => setV({ ...v, [k]: x });
  return (
    <Modal title="Списание сырья вне рецептуры" onClose={onClose} actions={<>
      <Button onClick={onClose}>Отмена</Button>
      <Button variant="primary" disabled={!v.material_id || !(Number(v.qty.replace(",", ".")) > 0) || v.reason.trim().length < 2} onClick={async () => {
        try { await api.post("/stock/writeoffs", { ...v, qty: Number(v.qty.replace(",", ".")) }); toast.ok("Списано"); onDone(); } catch (e) { toast.error(e); }
      }}>Списать</Button>
    </>}>
      {!mats.data ? <Loading error={mats.error} /> : (
        <div className="form-grid">
          {plants.length > 1 && <Field label="Завод"><Select value={v.plant_id} onChange={(e) => set("plant_id", e.target.value)} options={plants.map((p) => ({ value: p.id, label: p.name }))} /></Field>}
          <Field label="Материал" wide><Select value={v.material_id} onChange={(e) => set("material_id", e.target.value)} placeholder="Выберите"
            options={mats.data.items.map((x: any) => ({ value: x.id, label: `${x.name} (${x.unit})` }))} /></Field>
          <Field label="Количество"><Input inputMode="decimal" value={v.qty} onChange={(e) => set("qty", e.target.value)} /></Field>
          <Field label="Причина" wide hint="Например: промывка бункера, порча, пересыпка на другой завод"><Input value={v.reason} onChange={(e) => set("reason", e.target.value)} /></Field>
        </div>
      )}
    </Modal>
  );
}

export function InventoryDialog({ plants, plantId, onClose, onDone }: { plants: { id: string; name: string }[]; plantId?: string; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [plant, setPlant] = useState(plantId || plants[0]?.id || "");
  const mats = useMaterials();
  const bal = useAsync<{ rows: Balance[] }>(() => api.get("/stock/balances", { plant }), [plant]);
  const [fact, setFact] = useState<Record<string, string>>({});
  const [comment, setComment] = useState("");
  const book = (id: string) => bal.data?.rows.find((r) => r.material_id === id)?.balance ?? 0;
  const lines = Object.entries(fact).filter(([, x]) => x.trim() !== "").map(([material_id, x]) => ({ material_id, fact: Number(x.replace(",", ".")) }));
  return (
    <Modal title="Инвентаризация" onClose={onClose} actions={<>
      <Button onClick={onClose}>Отмена</Button>
      <Button variant="primary" disabled={!lines.length || lines.some((l) => !(l.fact >= 0))} onClick={async () => {
        try {
          const r = await api.post("/stock/inventories", { plant_id: plant, lines, comment: comment || null });
          const n = r.lines.filter((l: any) => l.diff).length;
          toast.ok(n ? `Проведено: расхождений ${n}, остатки поправлены` : "Проведено: расхождений нет");
          onDone();
        } catch (e) { toast.error(e); }
      }}>Провести</Button>
    </>}>
      {plants.length > 1 && <Field label="Завод"><Select value={plant} onChange={(e) => { setPlant(e.target.value); setFact({}); }} options={plants.map((p) => ({ value: p.id, label: p.name }))} /></Field>}
      {!mats.data || !bal.data ? <Loading error={mats.error || bal.error} /> : (
        <div className="stack tight">
          <p className="small muted" style={{ margin: 0 }}>Впишите, сколько есть по факту (замер, взвешивание). Пустые строки не меняются. «По учёту» — сколько в системе сейчас.</p>
          <table className="table">
            <thead><tr><th>Материал</th><th className="num">По учёту</th><th className="num">По факту</th><th className="num">Разница</th></tr></thead>
            <tbody>
              {mats.data.items.filter((m: any) => m.active).map((m: any) => {
                const f = fact[m.id];
                const diff = f && f.trim() !== "" ? Number(f.replace(",", ".")) - book(m.id) : null;
                return (
                  <tr key={m.id}>
                    <td>{m.name}<div className="small muted">{m.unit}</div></td>
                    <td className="num">{fmtNum(book(m.id), 3)}</td>
                    <td className="num"><Input inputMode="decimal" style={{ width: 110 }} value={f || ""} onChange={(e) => setFact({ ...fact, [m.id]: e.target.value })} aria-label={`По факту: ${m.name}`} /></td>
                    <td className={`num${diff ? " strong" : ""}`}>{diff == null || Number.isNaN(diff) ? "" : `${diff > 0 ? "+" : ""}${fmtNum(diff, 3)}`}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Field label="Комментарий" hint="Кто считал, как мерили"><Input value={comment} onChange={(e) => setComment(e.target.value)} /></Field>
        </div>
      )}
    </Modal>
  );
}

export function ReceiptsTable({ rows, onCancel, onControl }: { rows: any[]; onCancel?: (r: any) => void; onControl?: (r: any) => void }) {
  if (!rows.length) return <p className="empty">Приходов за период нет.</p>;
  return (
    <div className="panel table-wrap">
      <table className="table">
        <thead><tr><th>Когда</th><th>Завод</th><th>Материал</th><th className="num">Количество</th><th>Поставщик, документ</th><th>Входной контроль</th><th /></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td className="tabular">{fmtDateTime(r.at)}</td>
              <td>{r.plant_name}</td>
              <td>{r.material}</td>
              <td className="num">{qty(r.qty, r.unit)}</td>
              <td>{r.supplier || "—"}<div className="small muted">{[r.document_no, r.vehicle_plate].filter(Boolean).join(" · ")}</div></td>
              <td><span className={`tag ${LAB[r.lab_status]?.cls}`}>{LAB[r.lab_status]?.label}</span>{r.lab_comment && <div className="small muted">{r.lab_comment}</div>}</td>
              <td className="right">
                <div className="row tight" style={{ justifyContent: "flex-end" }}>
                  {onControl && r.lab_status === "pending" && <Button size="small" variant="primary" onClick={() => onControl(r)}>Проверить</Button>}
                  {onCancel && <Button size="small" variant="ghost" onClick={() => onCancel(r)}>Отменить</Button>}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Входной контроль партии: показатели и вывод лаборатории. */
export function ControlDialog({ receipt, onClose, onDone }: { receipt: any; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const preset: Record<string, string[]> = {
    cement: ["Нормальная густота, %", "Начало схватывания, мин", "Активность (прочность), МПа"],
    sand: ["Модуль крупности", "Пылевидные и глинистые частицы, %", "Влажность, %"],
    gravel: ["Фракция, мм", "Пылевидные и глинистые частицы, %", "Лещадность, %"],
    additive: ["Плотность, г/см³", "Внешний вид"],
  };
  const kind = receipt.material.toLowerCase().includes("цемент") ? "cement" : receipt.material.toLowerCase().includes("песок") ? "sand"
    : receipt.material.toLowerCase().includes("щеб") ? "gravel" : "additive";
  const [rows, setRows] = useState<{ name: string; value: string; norm: string }[]>(() => (preset[kind] || []).map((name) => ({ name, value: "", norm: "" })));
  const [comment, setComment] = useState("");
  const send = async (accepted: boolean) => {
    try {
      await api.post(`/stock/receipts/${receipt.id}/control`, { accepted, comment: comment || null, results: rows.filter((r) => r.name.trim() && r.value.trim()) });
      toast.ok(accepted ? "Партия годна" : "Партия не годна — возврат поставщику, остаток уменьшен");
      onDone();
    } catch (e) { toast.error(e); }
  };
  return (
    <Modal title={`Входной контроль: ${receipt.material}, ${qty(receipt.qty, receipt.unit)}`} onClose={onClose} actions={<>
      <Button onClick={onClose}>Позже</Button>
      <Button variant="danger" disabled={comment.trim().length < 2} title="Нужна причина" onClick={() => send(false)}>Не годен</Button>
      <Button variant="primary" onClick={() => send(true)}>Годен</Button>
    </>}>
      <div className="stack tight">
        <div className="small muted">{receipt.plant_name} · {fmtDateTime(receipt.at)} · {receipt.supplier || "поставщик не указан"} {receipt.document_no ? `· ${receipt.document_no}` : ""}</div>
        <table className="table">
          <thead><tr><th>Показатель</th><th>Значение</th><th>Норма</th></tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td><Input value={r.name} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} aria-label="Показатель" /></td>
                <td><Input value={r.value} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))} aria-label={`Значение: ${r.name}`} style={{ width: 110 }} /></td>
                <td><Input value={r.norm} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, norm: e.target.value } : x)))} aria-label={`Норма: ${r.name}`} style={{ width: 110 }} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        <div><Button size="small" variant="ghost" onClick={() => setRows([...rows, { name: "", value: "", norm: "" }])}>+ показатель</Button></div>
        <Field label="Заключение" hint="Для «не годен» обязательно: почему"><Input value={comment} onChange={(e) => setComment(e.target.value)} /></Field>
      </div>
    </Modal>
  );
}
